import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiArrowLeft, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import VaultNoteDetail from './vault_note_detail';
import VaultConfirmModal from './vault_confirm_modal';
import VaultCompose from './vault_compose';
import VaultHeader from './vault_header';
import './css/vault.css';

function buildSuggestedTags(notes) {
  const counts = new Map();
  for (const note of notes) {
    const raw = note.tags || note.tag || '';
    String(raw)
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t && t !== 'heart' && !t.startsWith('__folder_'))
      .forEach((t) => {
        const key = t.toLowerCase();
        const prev = counts.get(key);
        if (prev) prev.count += 1;
        else counts.set(key, { label: t, count: 1 });
      });
  }
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .map((x) => x.label)
    .slice(0, 40);
}

const VaultMobile = ({
  documents,
  onSearchResults,
  onAddDocument,
  onUpdateNote,
  onFileUpload,
  onFileRemove,
  pendingUploads,
  selectedNoteId,
  setSelectedNoteId,
  onShareSaved,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const draftTitleRef = useRef(null);
  const notes = documents ? Array.from(documents.values()) : [];
  const selectedNote = notes.find((n) => String(n.vault_id) === String(selectedId));
  const suggestedTags = useMemo(() => buildSuggestedTags(notes), [notes]);
  const canCreate = draftTitle.trim().length > 0 && !isCreating;

  useEffect(() => {
    if (selectedNoteId) {
      setSelectedId(selectedNoteId);
      setIsComposing(false);
    }
  }, [selectedNoteId]);

  const clearStagedFiles = () => {
    stagedFiles.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setStagedFiles([]);
  };

  const cancelCompose = () => {
    setIsComposing(false);
    setDraftTitle('');
    setDraftBody('');
    clearStagedFiles();
    setCreateError('');
    setIsCreating(false);
  };

  const startCompose = () => {
    setIsComposing(true);
    setDraftTitle('');
    setDraftBody('');
    clearStagedFiles();
    setCreateError('');
    setSelectedId(null);
    setSelectedNoteId?.(null);
    requestAnimationFrame(() => draftTitleRef.current?.focus());
  };

  const submitCompose = async () => {
    const title = draftTitle.trim();
    if (!title) {
      setCreateError('Add a title to create this note.');
      draftTitleRef.current?.focus();
      return;
    }
    setIsCreating(true);
    setCreateError('');
    try {
      const filesToUpload = stagedFiles.map((s) => s.file);
      const vaultId = await onAddDocument(title, draftBody);
      if (vaultId != null && filesToUpload.length) {
        filesToUpload.forEach((file) => onFileUpload(vaultId, file));
      }
      stagedFiles.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setStagedFiles([]);
      setIsComposing(false);
      setDraftTitle('');
      setDraftBody('');
      setCreateError('');
      setIsCreating(false);
    } catch (err) {
      setCreateError(err?.message || 'Failed to create note.');
      setIsCreating(false);
    }
  };

  const requestDeleteNote = (vaultId, title) => {
    setDeleteTarget({ vaultId, title: title || 'Untitled' });
  };

  const cancelDeleteNote = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDeleteNote = () => {
    if (!deleteTarget || isDeleting) return;
    const { vaultId } = deleteTarget;
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    setIsDeleting(true);
    axios
      .get(`/api/vault/delete?user_id=${user_id}&vault_id=${vaultId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setDeleteTarget(null);
        setSelectedId(null);
        setSelectedNoteId?.(null);
        onSearchResults?.(query);
      })
      .catch(() => alert('Failed to delete note.'))
      .finally(() => {
        setIsDeleting(false);
      });
  };

  let body;
  if (isComposing) {
    body = (
      <VaultCompose
        title={draftTitle}
        body={draftBody}
        onTitleChange={(v) => {
          setDraftTitle(v);
          if (createError) setCreateError('');
        }}
        onBodyChange={setDraftBody}
        stagedFiles={stagedFiles}
        onStagedFilesChange={setStagedFiles}
        onCancel={cancelCompose}
        onSubmit={submitCompose}
        isCreating={isCreating}
        createError={createError}
        canCreate={canCreate}
        titleRef={draftTitleRef}
      />
    );
  } else if (selectedNote) {
    body = (
      <>
        <div className="vault-mobile-topbar">
          <button
            type="button"
            className="vault-icon-btn"
            onClick={() => {
              setSelectedId(null);
              setSelectedNoteId?.(null);
            }}
          >
            <FiArrowLeft />
          </button>
          {selectedNote.can_manage_sharing && (
            <button
              type="button"
              className="vault-icon-btn"
              onClick={() => requestDeleteNote(selectedNote.vault_id, selectedNote.title)}
            >
              <FiTrash2 />
            </button>
          )}
        </div>
        <main className="main-panel">
          <VaultNoteDetail
            note={selectedNote}
            onUpdateNote={onUpdateNote}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            pendingUploads={pendingUploads}
            suggestedTags={suggestedTags}
            onShareSaved={(settings) => onShareSaved?.(selectedNote.vault_id, settings)}
          />
        </main>
      </>
    );
  } else {
    body = (
      <>
        <button type="button" className="vault-new-note-btn" onClick={startCompose}>
          <FiPlus />
          <span>New note</span>
        </button>
        <div className="search-bar">
          <FiSearch className="icon" />
          <input
            type="text"
            placeholder="Search notes…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onSearchResults?.(e.target.value);
            }}
          />
        </div>
        <div className="note-list">
          {notes.map((n, index) => (
            <div
              key={n.vault_id}
              className="note-list-item"
              onClick={() => {
                setSelectedId(n.vault_id);
                setSelectedNoteId?.(n.vault_id);
              }}
            >
              <span className="note-list-serial" aria-hidden="true">
                {index + 1}
              </span>
              <div className="note-list-item-main">
                <div className="note-list-item-title">{n.title || 'Untitled'}</div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="vault-shell vault-shell--mobile">
      <VaultConfirmModal
        open={!!deleteTarget}
        title="Delete note?"
        message={
          deleteTarget
            ? localStorage.getItem('walmates-login_id') === 'walmates'
              ? `Permanently delete “${deleteTarget.title}” for everyone? This cannot be undone.`
              : `Remove “${deleteTarget.title}” from your vault? It will remain available to Walmates.`
            : ''
        }
        confirmLabel="Delete"
        busy={isDeleting}
        onConfirm={confirmDeleteNote}
        onCancel={cancelDeleteNote}
      />
      <VaultHeader noteCount={notes.length} />
      {body}
    </div>
  );
};

export default VaultMobile;
